import React, { useState, useEffect } from 'react';
import { useCSVStore } from '../../stores/csvStore';
import { analyzeData } from '../../services/bedrockApi';
import ReportPopup from './ReportPopup';
import './AIAnalysisButton.css';

interface CacheStats {
  size: number;
  types: Record<string, number>;
}

// Hardcoded Claude model ID - use the Inference Profile ARN directly
const CLAUDE_37_SONNET_MODEL_ID = 'arn:aws:bedrock:eu-central-1::inference-profile/eu.anthropic.claude-3-7-sonnet-20250219-v1:0';


const DEFAULT_PROMPT = 
  "You are an automotive financial accountant. Please analyze the data provided in the file, " +
  "and for the most recent data using year and month provide a focused summary of key patterns, " + 
  "trends or outliers compared with other data in the file. " +
  "IMPORTANT FORMATTING: " +
  "1. Keep your written analysis brief and to the point, focusing on 3-5 key insights " +
  "2. Prioritize quality visualizations over lengthy text explanations " +
  "3. Use visualizations to show trends rather than explaining them extensively in text " +
  "IMPORTANT DATE HANDLING: Columns with names containing 'date', 'month', 'year', or time-related terms " +
  "should be treated as dates, not numbers. When referring to dates in your analysis text: " +
  "1. Always format them as proper date strings (like 'January 2023', '2023-01-15', etc.), never as plain numbers " +
  "2. Maintain chronological order in tables, lists, or when mentioning date sequences " +
  "3. Do not convert YYYY-MM-DD or any date formats to numeric values anywhere in your response " +
  "4. When discussing trends over time, refer to dates by their proper month/year names, not as numbers " +
  "Format your response with clear sections using markdown headings. " +
  "When presenting data that should be visualized, you MUST include a dedicated JSON section for each visualization using the format below. " +
  "This format is specifically designed for Chart.js and must be followed exactly:\n\n" +
  "```visualization\n" +
  "{\n" +
  "  \"type\": \"bar\",\n" +
  "  \"title\": \"Example Chart Title\",\n" +
  "  \"description\": \"Brief description of what this visualization shows\",\n" +
  "  \"labels\": [\"Jan\", \"Feb\", \"Mar\"],\n" +
  "  \"datasets\": [\n" +
  "    {\n" +
  "      \"label\": \"Dataset Name\",\n" +
  "      \"data\": [10, 20, 30]\n" +
  "    }\n" +
  "  ],\n" +
  "  \"xAxisLabel\": \"Months\",\n" +
  "  \"yAxisLabel\": \"Values\"\n" +
  "}\n" +
  "```\n\n" +
  "Important: The visualization JSON must be valid and complete. For date fields, use properly formatted date strings in labels. " +
  "Valid chart types are: bar, line, pie, scatter, doughnut, radar, horizontalBar, horizontalbar. " +
  "Always include at least 2-3 visualizations where appropriate to highlight key insights from the data. " +
  "For pie and doughnut charts, include only one dataset.";

interface AIAnalysisButtonProps {
  prompt?: string;
  onAnalysisComplete?: (result: string) => void;
  customPrompt?: string;
  modelId?: string;
}

// Default props
const defaultProps: AIAnalysisButtonProps = {
  prompt: DEFAULT_PROMPT
};

const AIAnalysisButton: React.FC<AIAnalysisButtonProps> = (props) => {
  const { csvData, uploadedFile } = useCSVStore();
  const { prompt, onAnalysisComplete, customPrompt, modelId } = { ...defaultProps, ...props };
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null);
  const [showCacheControls, setShowCacheControls] = useState(false);
  const [isCacheEnabled, setIsCacheEnabled] = useState(true);

  // Fetch cache stats on initial load and when cache is updated
  useEffect(() => {
    if (showCacheControls) {
      fetchCacheStats();
    }
  }, [showCacheControls]);

  const fetchCacheStats = async () => {
    try {
      const response = await fetch('/api/ai/cache/stats');
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCacheStats(data.stats);
        }
      }
    } catch (err) {
      console.error('Failed to fetch cache stats:', err);
    }
  };

  const clearCache = async () => {
    try {
      const response = await fetch('/api/ai/cache/clear', {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert('Cache cleared successfully');
          fetchCacheStats();
        }
      }
    } catch (err) {
      console.error('Failed to clear cache:', err);
    }
  };

  const toggleCaching = async () => {
    try {
      const newState = !isCacheEnabled;
      const response = await fetch('/api/ai/cache/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          enabled: newState
        })
      });
      
      if (response.ok) {
        setIsCacheEnabled(newState);
        alert(`Caching ${newState ? 'enabled' : 'disabled'}`);
      }
    } catch (err) {
      console.error('Failed to toggle caching:', err);
    }
  };

  const toggleCacheControls = () => {
    setShowCacheControls(!showCacheControls);
    if (!showCacheControls) {
      fetchCacheStats();
    }
  };

  const getModelDisplayName = () => {
    return 'Claude 3.7 Sonnet (Inference Profile)';
  };

  const openReport = () => {
    setIsReportOpen(true);
  };

  const closeReport = () => {
    setIsReportOpen(false);
  };

  const requestAnalysis = async () => {
    if (!csvData || csvData.length <= 1) {
      setError("No data available for analysis");
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setAnalysisResult(null);

    try {
      const headers = csvData[0];
      const rows = csvData.slice(1);
      const dateColumnIndices: number[] = [];
      headers.forEach((header, index) => {
        const headerLower = header.toString().toLowerCase();
        if (
          headerLower.includes('date') || 
          headerLower.includes('year') || 
          headerLower.includes('month') ||
          headerLower.includes('time')
        ) {
          dateColumnIndices.push(index);
        }
      });
      const request = {
        prompt: prompt || DEFAULT_PROMPT,
        data: {
          headers,
          rows,
          fileName: uploadedFile?.name || 'financial_data',
          fileType: uploadedFile?.fileType || 'csv',
          dateColumns: dateColumnIndices.map(i => headers[i])
        },
        modelId: CLAUDE_37_SONNET_MODEL_ID
      };
      const result = await analyzeData(request);
      setAnalysisResult(result);
      if (onAnalysisComplete) {
        onAnalysisComplete(result);
      }
    } catch (err: any) {
      console.error('Analysis error:', err);
      setError(err.message || 'An error occurred during analysis');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Format numbers and percentages in text
  const formatTextWithNumbers = (text: string) => {
    // Format percentages like "XX.X%" or "XX%" with proper styling
    const percentRegex = /(\d+(\.\d+)?\s*%)/g;
    
    // Format large numbers with commas
    const numberRegex = /\b(\d{4,}(\.\d+)?)\b/g;
    
    // Format currency values like "$XX.XX" or "$XX,XXX.XX"
    const currencyRegex = /(\$\s*\d{1,3}(,\d{3})*(\.\d+)?)/g;
    
    // Apply formatting in order
    let formattedText = text
      .replace(percentRegex, '<span class="percentage-value">$1</span>')
      .replace(numberRegex, (match) => {
        // Don't format if it's already part of a currency value
        if (text.includes('$' + match) || text.includes('$ ' + match)) return match;
        
        // Format with commas for thousands
        const num = parseFloat(match);
        return '<span class="number-value">' + num.toLocaleString() + '</span>';
      })
      .replace(currencyRegex, '<span class="currency-value">$1</span>');
    
    return formattedText;
  };

  const formatAnalysisResult = (text: string) => {
    if (!text) return null;
    
    // Process the text to extract sections
    const lines = text.split('\n');
    const formattedSections: {
      title: string;
      content: React.ReactNode[];
      isList: boolean;
    }[] = [];
    
    let currentSection: {
      title: string;
      content: React.ReactNode[];
      isList: boolean;
    } = {
      title: '',
      content: [],
      isList: false
    };
    
    // Process line by line
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines
      if (!line) {
        if (currentSection.content.length > 0) {
          currentSection.content.push(<br key={`br-${i}`} />);
        }
        continue;
      }
      
      // Check if this is a header line
      if ((line.startsWith('# ') || 
           line.startsWith('## ') || 
           line.startsWith('### ') ||
           line.match(/^[A-Z][A-Za-z\s]+:/) ||
           (line.length < 80 && i < lines.length - 1 && !lines[i+1].trim())) && 
          !line.startsWith('- ') && 
          !line.match(/^\d+\./)) {
        
        // If we have content in the current section, save it
        if (currentSection.title || currentSection.content.length) {
          formattedSections.push({...currentSection});
        }
        
        // Start a new section
        currentSection = {
          title: line.replace(/^#+\s+/, '').replace(/:$/, ''),
          content: [],
          isList: false
        };
        continue;
      }
      
      // Check if this is a list item
      if (line.startsWith('- ') || line.startsWith('* ') || line.match(/^\d+\./)) {
        if (!currentSection.isList) {
          currentSection.isList = true;
        }
        
        // Format the list item
        const itemText = line.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '');
        currentSection.content.push(
          <li 
            key={`li-${i}`} 
            className="analysis-list-item" 
            dangerouslySetInnerHTML={{ __html: formatTextWithNumbers(itemText) }}
          />
        );
        continue;
      }
      
      // Regular paragraph text
      if (currentSection.isList) {
        // If we were in a list but this is not a list item, close the list
        formattedSections.push({...currentSection});
        currentSection = {
          title: '',
          content: [line],
          isList: false
        };
      } else {
        // Add to current paragraph
        currentSection.content.push(
          <p 
            key={`p-${i}`} 
            className="analysis-paragraph" 
            dangerouslySetInnerHTML={{ __html: formatTextWithNumbers(line) }}
          />
        );
      }
    }
    
    // Add the last section if not empty
    if (currentSection.title || currentSection.content.length) {
      formattedSections.push({...currentSection});
    }
    
    // Render the formatted sections
    return (
      <div className="analysis-sections">
        {formattedSections.map((section, index) => (
          <div key={`section-${index}`} className="analysis-section">
            {section.title && (
              <h4 className="section-title">{section.title}</h4>
            )}
            {section.isList ? (
              <ul className="analysis-list">
                {section.content}
              </ul>
            ) : (
              <div className="section-content">
                {section.content}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="ai-analysis-container">
      {!analysisResult && !isAnalyzing && (
        <>
          <div className="ai-model-info">
            <h3>AI Analysis with Claude 3.7 Sonnet (Inference Profile)</h3>
            <p>Analyze your financial data using an AWS Bedrock Inference Profile to access Claude 3.7 Sonnet with configured parameters.</p>
            <button 
              className="cache-controls-toggle"
              onClick={toggleCacheControls}
            >
              {showCacheControls ? 'Hide' : 'Show'} Cache Controls
            </button>
            
            {showCacheControls && (
              <div className="cache-controls">
                <div className="cache-stats">
                  <h4>Cache Statistics</h4>
                  {cacheStats ? (
                    <div>
                      <p>Total cached items: {cacheStats.size}</p>
                      <p>Types: {Object.entries(cacheStats.types || {}).map(([k, v]) => `${k} (${v})`).join(', ')}</p>
                    </div>
                  ) : (
                    <p>Loading cache stats...</p>
                  )}
                </div>
                <div className="cache-actions">
                  <button 
                    className="cache-button"
                    onClick={clearCache}
                  >
                    Clear Cache
                  </button>
                  <button 
                    className="cache-button"
                    onClick={toggleCaching}
                  >
                    {isCacheEnabled ? 'Disable' : 'Enable'} Caching
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <button 
            className="analyze-button"
            onClick={requestAnalysis}
            disabled={isAnalyzing}
          >
            Analyze with {getModelDisplayName()}
          </button>
        </>
      )}
      
      {isAnalyzing && (
        <div className="analysis-loading">
          <div className="spinner"></div>
          <p>Analyzing your financial data with {getModelDisplayName()}...</p>
          <p className="analysis-tip">This may take a minute for large datasets.</p>
        </div>
      )}
      
      {error && (
        <div className="analysis-error">
          <p>❌ Error: {error}</p>
          <button className="try-again-button" onClick={() => setError(null)}>
            Try Again
          </button>
        </div>
      )}
      
      {analysisResult && (
        <div className="analysis-completed">
          <p>Analysis complete! Your financial data has been analyzed.</p>
          <button className="view-report-button" onClick={openReport}>
            View Full Report
          </button>
        </div>
      )}
      
      {/* Report Popup */}
      {analysisResult && (
        <ReportPopup
          isOpen={isReportOpen}
          onClose={closeReport}
          analysisResult={analysisResult}
          fileName={uploadedFile?.name ? `Analysis of ${uploadedFile.name}` : 'Financial Analysis Report'}
        />
      )}
    </div>
  );
};

export default AIAnalysisButton; 